<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '../i18n';
import { displayName, type ProfileRecord } from '../profiles/registry';
import { tryPin } from '../profiles/pin';
import ProfileAvatar from './ProfileAvatar.vue';

/**
 * Entering a profile's PIN: at start and on the lock screen. Wrong tries are counted in the registry;
 * after five the field waits (a countdown), a reload does not help.
 */
const props = defineProps<{ profile: ProfileRecord }>();
const emit = defineEmits<{ ok: [] }>();

const pin = ref('');
const busy = ref(false);
const error = ref('');
const until = ref(props.profile.lockedUntil > Date.now() ? props.profile.lockedUntil : 0);
const now = ref(Date.now());
const field = ref<HTMLInputElement | null>(null);
let tick: ReturnType<typeof setInterval> | null = null;

const waiting = computed(() => Math.max(0, until.value - now.value));
const waitText = computed(() => {
  const s = Math.ceil(waiting.value / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});

onMounted(() => {
  tick = setInterval(() => {
    now.value = Date.now();
    if (until.value && now.value >= until.value) {
      until.value = 0;
      void nextTick(() => field.value?.focus());
    }
  }, 500);
  field.value?.focus();
});
onBeforeUnmount(() => {
  if (tick) clearInterval(tick);
});

async function submit(): Promise<void> {
  if (busy.value || waiting.value || !pin.value) return;
  busy.value = true;
  error.value = '';
  const r = await tryPin(props.profile.id, pin.value);
  busy.value = false;
  pin.value = '';
  if (r.ok) {
    emit('ok');
    return;
  }
  if (r.wait) until.value = Date.now() + r.wait;
  else error.value = t('profiles.pinWrong', { n: r.triesLeft });
  void nextTick(() => field.value?.focus());
}
</script>

<template>
  <form class="pin-form" @submit.prevent="submit">
    <ProfileAvatar :id="profile.id" :first-name="profile.firstName" :last-name="profile.lastName" :avatar="profile.avatar" :size="72" />
    <strong class="who">{{ displayName(profile) }}</strong>
    <input
      ref="field"
      v-model="pin"
      class="pin-input"
      type="password"
      inputmode="numeric"
      autocomplete="off"
      maxlength="8"
      :placeholder="t('profiles.pinPlaceholder')"
      :aria-label="t('profiles.pin')"
      :disabled="!!waiting"
      data-field="pin"
      @input="pin = pin.replace(/\D/g, '')"
    />
    <p v-if="waiting" class="msg warn" role="alert" data-state="pin-wait">{{ t('profiles.pinWait', { time: waitText }) }}</p>
    <p v-else-if="error" class="msg warn" role="alert" data-state="pin-wrong">{{ error }}</p>
    <button class="pill primary tall" type="submit" :disabled="busy || !!waiting || pin.length < 4" data-act="unlock">{{ t('profiles.unlock') }}</button>
    <slot />
  </form>
</template>

<style scoped>
.pin-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.who {
  font-size: 17px;
}
.pin-input {
  width: 200px;
  height: 44px;
  text-align: center;
  font-size: 22px;
  letter-spacing: 0.4em;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--bg);
  color: var(--text);
}
.pin-input::placeholder {
  letter-spacing: normal;
  font-size: 14px;
}
.pin-input:focus {
  outline: none;
  border-color: var(--accent);
}
.msg {
  margin: 0;
  font-size: 13px;
}
.warn {
  color: var(--warn);
}
.pill.tall {
  min-width: 200px;
  justify-content: center;
}
</style>
